import type { BaseAPIResponse } from '~/types/api';
import type { AuthenticationSettings, Business, BusinessSettings, OAuthClient } from '~/types/business';
import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';

// Company profile, settings, contact records (addresses / domains / phone
// numbers), authentication + SCIM configuration, and API access credentials.
export const companyService = {
    /**
     * Get business data by slug or company ID
     */
    async getBusiness(identifier: string | number): Promise<Business> {
        // Number → id-keyed show endpoint; string → slug-lookup
        // endpoint. We do NOT coerce all-digit strings to ids: route
        // params are always strings, and a numeric slug like
        // "12345" must still be resolved through the slug lookup or
        // it would silently misroute to the wrong company.
        const path = typeof identifier === 'number'
            ? `/companies/${identifier}`
            : `/public/companies/lookup/${identifier}`;

        return await pbRequest<Business>(path, {
            failure: 'Failed to fetch business data',
        });
    },

    /**
     * Get business data with apps by company ID
     * This endpoint returns full business data including apps/permissions
     */
    async getBusinessWithApps(companyId: number): Promise<Business> {
        return await pbRequest<Business>(`/companies/${companyId}/apps`, {
            failure: 'Failed to fetch business apps',
        });
    },

    /**
     * Get business settings
     */
    async getSettings(companyId: number): Promise<BusinessSettings> {
        return await pbRequest<BusinessSettings>(`/companies/${companyId}/settings`, {
            failure: 'Failed to fetch business settings',
        });
    },

    /**
     * Update business settings
     */
    async updateSettings(companyId: number, settings: Partial<BusinessSettings>): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/settings`, {
            method: 'PATCH',
            body: settings,
            expect: [200, 204],
            failure: 'Failed to update business settings',
        });
    },

    /**
     * Upload business logo
     */
    async uploadLogo(companyId: number, file: File): Promise<string> {
        const formData = new FormData();
        formData.append('logo', file);

        const envelope = await pbRequestRaw<BaseAPIResponse<{ logo: string }>>(
            `/companies/${companyId}/settings/logo`,
            {
                method: 'POST',
                body: formData,
                failure: 'Failed to upload logo',
            }
        );

        return envelope.data.logo;
    },

    /**
     * Save phone number (create or update)
     */
    async savePhoneNumber(
        companyId: number,
        phoneData: { id?: number; country_code: string; phone_number: string; type: string }
    ): Promise<any> {
        const method = phoneData.id ? 'PATCH' : 'POST';
        const url = phoneData.id
            ? `/companies/${companyId}/settings/phone-numbers/${phoneData.id}`
            : `/companies/${companyId}/settings/phone-numbers`;

        return await pbRequest<any>(url, {
            method,
            body: phoneData,
            expect: [200, 201],
            failure: 'Failed to save phone number',
        });
    },

    /**
     * Delete phone number
     */
    async deletePhoneNumber(companyId: number, phoneId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/settings/phone-numbers/${phoneId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to delete phone number',
        });
    },

    // ==================== Addresses ====================

    /**
     * Save address (create or update)
     */
    async saveAddress(companyId: number, addressData: any): Promise<any> {
        const method = addressData.id ? 'PATCH' : 'POST';
        const url = addressData.id
            ? `/companies/${companyId}/settings/addresses/${addressData.id}`
            : `/companies/${companyId}/settings/addresses`;

        return await pbRequest<any>(url, {
            method,
            body: addressData,
            expect: [200, 201],
            failure: 'Failed to save address',
        });
    },

    /**
     * Delete address
     */
    async deleteAddress(companyId: number, addressId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/settings/addresses/${addressId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to delete address',
        });
    },

    // ==================== Domains ====================

    /**
     * Save domain (create or update)
     */
    async saveDomain(companyId: number, domainData: { id?: number; domain: string; is_primary?: boolean }): Promise<any> {
        const method = domainData.id ? 'PATCH' : 'POST';
        const url = domainData.id ? `/companies/${companyId}/domains/${domainData.id}` : `/companies/${companyId}/domains`;

        return await pbRequest<any>(url, {
            method,
            body: domainData,
            expect: [200, 201],
            failure: 'Failed to save domain',
        });
    },

    /**
     * Delete domain
     */
    async deleteDomain(companyId: number, domainId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/domains/${domainId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to delete domain',
        });
    },

    /**
     * Verify domain
     */
    async verifyDomain(companyId: number, domainId: number): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/domains/${domainId}/verify`, {
            method: 'POST',
            failure: 'Domain verification failed',
        });
    },

    async getPhoneNumbers(companyId: number): Promise<any[]> {
        return await pbRequest<any[]>(`/companies/${companyId}/phone-numbers`, {
            failure: 'Failed to fetch phone numbers',
        });
    },

    async storePhoneNumber(
        companyId: number,
        phoneData: { country_code: string; phone_number: string; type?: string }
    ): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/phone-numbers`, {
            method: 'POST',
            body: phoneData,
            expect: [200, 201],
            failure: 'Failed to add phone number',
        });
    },

    async updatePhoneNumber(
        companyId: number,
        phoneId: number,
        phoneData: { country_code?: string; phone_number?: string }
    ): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/phone-numbers/${phoneId}`, {
            method: 'PATCH',
            body: phoneData,
            failure: 'Failed to update phone number',
        });
    },

    // ==================== Addresses ====================

    async getAddresses(companyId: number): Promise<any[]> {
        return await pbRequest<any[]>(`/companies/${companyId}/addresses`, {
            failure: 'Failed to fetch addresses',
        });
    },

    async storeAddress(companyId: number, addressData: {
        address_line_1: string;
        address_line_2?: string;
        address_city: string;
        address_region: string;
        address_postal_code: string;
        address_country: string;
    }): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/addresses`, {
            method: 'POST',
            body: addressData,
            expect: [200, 201],
            failure: 'Failed to add address',
        });
    },

    async updateAddress(companyId: number, addressId: number, addressData: {
        address_line_1?: string;
        address_line_2?: string;
        address_city?: string;
        address_region?: string;
        address_postal_code?: string;
        address_country?: string;
    }): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/addresses/${addressId}`, {
            method: 'PATCH',
            body: addressData,
            failure: 'Failed to update address',
        });
    },

    /**
     * Get authentication settings
     */
    async getAuthenticationSettings(companyId: number): Promise<AuthenticationSettings> {
        return await pbRequest<AuthenticationSettings>(`/companies/${companyId}/settings/authentication`, {
            failure: 'Failed to fetch authentication settings',
        });
    },

    /**
     * Update authentication settings
     */
    async updateAuthenticationSettings(companyId: number, settings: Partial<AuthenticationSettings>): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/settings/authentication`, {
            method: 'POST',
            body: settings,
            expect: [200, 204],
            failure: 'Failed to update authentication settings',
        });
    },

    /**
     * Generate a SCIM bearer token for the company
     */
    async generateScimToken(companyId: number): Promise<{ token: string }> {
        const envelope = await pbRequestRaw<{ data: { token: string } }>(
            `/companies/${companyId}/settings/scim-token`,
            {
                method: 'POST',
                expect: [200, 201],
                failure: 'Failed to generate SCIM token',
            }
        );

        return envelope.data;
    },

    /**
     * Revoke the SCIM bearer token for the company
     */
    async revokeScimToken(companyId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/settings/scim-token`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to revoke SCIM token',
        });
    },

    /**
     * The company's live OAuth clients. The endpoint is named for API keys and
     * still returns them alongside, but the business app mints and lists
     * OAuth clients only.
     */
    async getApiKeys(companyId: number): Promise<{ oauth_clients: OAuthClient[] }> {
        return await pbRequest<{ oauth_clients: OAuthClient[] }>(`/companies/${companyId}/api-keys`, {
            failure: 'Failed to fetch API keys',
        });
    },

    /**
     * Create OAuth client
     */
    async createOAuthClient(
        companyId: number,
        flow: 'authorization' | 'client_credentials',
        idempotencyKey: string,
        redirect?: string,
        generateToken?: boolean
    ): Promise<any> {
        const envelope = await pbRequestRaw<BaseAPIResponse<any>>(`/companies/${companyId}/oauth-clients`, {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: {
                flow,
                redirect: redirect || null,
                token: generateToken || false,
            },
            expect: [200, 201],
            failure: 'Failed to create OAuth client',
        });

        return envelope?.data;
    },

    /**
     * Delete OAuth client
     */
    async deleteOAuthClient(companyId: number, clientId: string): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/oauth-clients/${clientId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to delete OAuth client',
        });
    },
};
